import { shallowRef, triggerRef } from 'vue';

import { tokenStorage } from '@/platform/auth-runtime/token-storage';
import { reportManualError } from '@/platform/telemetry/frontend-alert';
import { isString } from '@/shared/utils/is';
import { parsePrivateLink } from '@/shared/utils/private-link';

// shallowRef + triggerRef so watchers see EVERY push, including two
// identical frames in a row. A regular ref short-circuits on same-primitive
// assignment, which silently drops repeated permission / account /
// notification pushes — backend sends the same JSON twice in legitimate
// cases (state change → revert → state change) and consumers would never
// rerun their handler for the second frame.
export const wsData = shallowRef<unknown>(null);

/**
 * Decodes one push frame; `wsData` holds the raw string. Never throws: an
 * exception inside a watcher kills the handler for every later push. The
 * error report omits the payload because a frame can carry user data.
 */
export function parseWsFrame(raw: unknown): Record<string, unknown> | undefined {
  if (!isString(raw) || !raw || raw === 'hb') return undefined;

  let msg: unknown;
  try {
    msg = JSON.parse(raw);
    // The backend stringifies some frames twice (e.g.
    // GlobalEventTypePermissionChanged).
    if (isString(msg)) msg = JSON.parse(msg);
  } catch (ex) {
    reportManualError(
      'ws_message_parse_error',
      (ex instanceof Error ? ex.message : String(ex)) || 'websocket error',
      { stack: ex instanceof Error ? ex.stack : 'No stack trace' },
    );
    return undefined;
  }

  if (!msg || typeof msg !== 'object') return undefined;
  return msg as Record<string, unknown>;
}

const INITIAL_RETRY_GAP = 1000;
const MAX_RETRY_GAP = 8000;
// Server pushes "hb" every 15s; 45s of inbound silence on an OPEN socket
// means the connection is half-open (no onclose fires for these).
const CONN_DEAD_MS = 45_000;
const LIVENESS_TICK_MS = 15_000;

let socket: WebSocket | null = null;
let hbTimer: ReturnType<typeof setInterval> | null = null;
let livenessTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let lastInboundTime = 0;
let retryGap = INITIAL_RETRY_GAP;
let stopped = true;
// connect() awaits the ticket POST, so the `socket` guard below cannot see a
// sibling call that is still in flight. Two of them would mint two single-use
// tickets and open two sockets, the second orphaning the first.
let connecting = false;

// Private-link installs sit on `<tenant>.privatelink.celerdata.com`; both the
// handshake and the ticket fetch must reach that backend, so both derive from
// the same env value.
function wsBaseUrl(): string {
  const base = parsePrivateLink(import.meta.env.VITE_WS_URL);
  return base.startsWith('/') ? `ws://${location.host}${base}` : base;
}

// SEC-195: the socket URL used to carry the week-long session token plus its
// signature as query params — readable in browser history and any proxy log. It
// carries a 30-second, single-use ticket now, minted by a normally-signed POST.
// Single-use is why this lives inside connect(): every reconnect mints a fresh
// one. The endpoint is a sibling of /websocket/connect and NOT under
// VITE_BASE_API's `/api`, hence the derived URL and empty baseURL.
async function fetchTicket(): Promise<string | null> {
  const url = parsePrivateLink(import.meta.env.VITE_WS_URL)
    .replace(/\/connect$/, '/ticket')
    .replace(/^ws/, 'http');
  try {
    // Lazy so the axios graph (interceptors → element-plus) stays out of this
    // module's eager import, which several specs mock around.
    const { axiosInstance } = await import('@/shared/lib/http/axios');
    const res = (await axiosInstance.post(url, undefined, { baseURL: '' })) as unknown as {
      ticket?: string;
    };
    return res?.ticket ?? null;
  } catch {
    // A dead session is 50001 and the response interceptor owns that (dialog +
    // redirect). Anything else is transient — fall through to the caller's
    // backoff rather than tearing the session down over one failed fetch.
    return null;
  }
}

function clearHeartbeat(): void {
  if (hbTimer) {
    clearInterval(hbTimer);
    hbTimer = null;
  }
}

function stopLivenessWatch(): void {
  if (livenessTimer) {
    clearInterval(livenessTimer);
    livenessTimer = null;
  }
}

function startLivenessWatch(): void {
  stopLivenessWatch();
  lastInboundTime = Date.now();
  livenessTimer = setInterval(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    if (lastInboundTime && Date.now() - lastInboundTime >= CONN_DEAD_MS) {
      // Half-open dead connection. close() → onclose → reconnect path.
      socket.close();
    }
  }, LIVENESS_TICK_MS);
}

function clearReconnect(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  reconnectTimer = setTimeout(() => void connect(), retryGap);
  retryGap = Math.min(retryGap * 2, MAX_RETRY_GAP);
}

async function connect(): Promise<void> {
  if (stopped || socket || connecting) return;
  connecting = true;
  try {
    await openSocket();
  } finally {
    connecting = false;
  }
}

async function openSocket(): Promise<void> {
  const auth = tokenStorage.read();
  if (!auth) return;
  const ticket = await fetchTicket();
  if (stopped) return;
  if (!ticket) {
    // No ticket, no socket. Retry on the dropped-connection backoff — giving up
    // would silently strand the tab with no pushes for the rest of the session.
    scheduleReconnect();
    return;
  }
  // Session may have changed while the ticket was in flight: stopWs() could
  // have been called (logout / account switch) OR the stored token itself
  // rotated. Opening this now would race the new session's socket or deliver
  // pushes scoped to the previous account.
  const current = tokenStorage.read();
  if (!current || current.token !== auth.token || current.ubid !== auth.ubid) return;
  // Ticket only. The old handshake also carried client_id; the ticket-based
  // endpoint does not take it, and sending it gets the handshake rejected.
  socket = new WebSocket(`${wsBaseUrl()}?${new URLSearchParams({ ticket })}`);
  socket.onopen = () => {
    retryGap = INITIAL_RETRY_GAP;
    clearHeartbeat();
    startLivenessWatch();
    hbTimer = setInterval(() => {
      // Guard the send: between the 25s tick and now the socket could have
      // moved to CLOSING/CLOSED (server-initiated close, transient drop). A
      // bare `socket?.send` would throw InvalidStateError in that window.
      // Skip the tick and let onclose handle the reconnect path.
      if (socket?.readyState === WebSocket.OPEN) socket.send('hb');
    }, 25000);
  };
  socket.onmessage = (event) => {
    lastInboundTime = Date.now();
    // Server sometimes emits `"hb"\n` (JSON-encoded + trailing newline)
    // instead of bare `hb`; both must be filtered or they leak into wsData.
    const data = event.data;
    if (typeof data === 'string' && (data === 'hb' || data.trim() === '"hb"')) return;
    // shallowRef short-circuits on Object.is equal values. Force a notify
    // when the new frame is identical to the last so watchers see EVERY push
    // — backend sends the same JSON twice in legitimate cases (state flips
    // back) and consumers would otherwise drop the second.
    const sameAsPrev = Object.is(wsData.value, data);
    wsData.value = data;
    if (sameAsPrev) triggerRef(wsData);
  };
  socket.onclose = () => {
    clearHeartbeat();
    stopLivenessWatch();
    socket = null;
    if (stopped) return;
    // A handshake rejected for a spent/expired ticket lands here as a close with
    // no status the browser exposes; the reconnect mints a new ticket, so it
    // self-heals. A dead session fails at the ticket POST instead.
    scheduleReconnect();
  };
}

export async function startWs(): Promise<void> {
  stopped = false;
  await connect();
}

export function stopWs(): void {
  // clearReconnect: a stopWs() landing between onclose and the queued
  // setTimeout fires would otherwise reconnect a logged-out session.
  stopped = true;
  clearHeartbeat();
  stopLivenessWatch();
  clearReconnect();
  socket?.close();
  socket = null;
}
